import { Injectable, inject } from "@angular/core";
import { CanDeactivateFn } from "@angular/router";
import { Store } from "@ngxs/store";
import * as TagsActions from "@vp/data-access/tags";
import { AuthenticationService } from "@vp/shared/authentication";
import { SharedConfirmationService } from "@vp/shared/confirmation";
import { TagManagerState } from "@vp/tag-manager/data-access/tag-manager-state";
import { TagManagerShellComponent } from "@vp/tag-manager/ui/tag-manager-shell";
import { Operation } from "rfc6902";
import { Observable, of } from "rxjs";
import { filter, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class TagManagerPendingChangesGuardService {
  pendingOperations$: Observable<Operation[]> = inject(Store).select(TagManagerState.operations);

  constructor(
    private readonly confirmationDialog: SharedConfirmationService,
    private authenticationService: AuthenticationService,
    private readonly store: Store
  ) {}

  canDeactivate(): Observable<boolean> | boolean {
    return new Observable<boolean>(observer => {
      this.pendingOperations$
        .pipe(
          take(1),
          switchMap(operations => {
            if (operations.length > 0) {
              return this.confirmationDialog
                .open(
                  "You have unsaved changes. They will be lost if you continue.",
                  "Continue",
                  "Pending Changes",
                  "tagManagerPendingChanges",
                  "30vw",
                  true,
                  true,
                  "warn"
                )
                .afterClosed();
            }
            return of(true);
          }),
          filter(confirmed => confirmed), // Only continue if confirmed is true
          withLatestFrom(this.authenticationService.isLoggedIn$()),
          tap(([, authResult]) => {
            if (!authResult.isAuthenticated) return;
            this.store.dispatch(new TagsActions.LoadTags(false));
          })
        )
        .subscribe(([confirmed]) => {
          observer.next(confirmed);
          observer.complete();
        });
    });
  }
}

export const TagManagerPendingChangesGuard: CanDeactivateFn<TagManagerShellComponent> = () => {
  return inject(TagManagerPendingChangesGuardService).canDeactivate();
};
